/* eslint-disable @stripe-internal/embedded/no-restricted-globals */
import type {LayerType} from '@sail/global-state';
import {deserializeError} from 'serialize-error';
import type {IUILayerRepositionValues} from '@sail/ui';
import {getPlatformOrigin} from '@stripe-internal/connect-embedded-lib';
import type {ConnectElementAttributes} from '../connect/ConnectElementAttributes';
import type {MessagePortValues} from './utils/messageChannel';
import {
  setupMessagePortHandoff,
  setupMessagePortListener,
} from './utils/messageChannel';
import type {
  OnboardingState,
  OnboardingStateValues,
} from '../data-layer-client/OnboardingContext/OnboardingState';
import type {AccountSessionClaim} from '../data-layer-frame/types';
import {DeferredPromise} from '../utils/DeferredPromise';
import type {
  ICollectFinancialConnectionsAccounts,
  IFromUILayerMessage,
  IToUILayerMessage,
  IUIDeleteAccessoryLayerFrom,
  IUILayerCallSupplementalFunctionParams,
  IUILayerInit,
  IUILayerReportSuccessValues,
  IUIReadjustIframe,
  UILayerInitOptions,
} from './UILayerMessages';
import type {ConnectJsOptions} from '../connect/ConnectJsOptions';
import {getDevLogger} from '../utils/getLogger';
import {getAccessoryLayer} from './utils/frameUtils';
import type {UpdateOptions} from '../connect/ConnectJSInterface/InitAndUpdateOptionsTypes';
import type {IConnectElementEvent} from '../connect/ConnectElementEventEmitter';
import type {ConnectElementRenderedState} from '../connect/ConnectElementRenderedState';
import {getIframeErrorReporter} from './utils/IframeErrorReporter';
import type {ConnectElementSupplementalObjects} from '../connect/ConnectElementSupplementalObjects';
import type {ConnectElementSupplementalFunctions} from '../connect/ConnectElementSupplementalFunctions';
import {generateSupplementalFunctionsProxy} from './SupplementalFunctionProxy';
import type {LoggedOutState} from '../connect/LoggedOutState';
import type {PlatformViewportState} from './PlatformViewportContext/PlatformViewportState';

const devLogger = getDevLogger();

type UILayerObservers = 'scrolloutside' | 'interactoutside';

export default class UILayerMessagePort {
  /**
   * The message port for the corresponding embedded component to communicate with the platform frame.
   */
  private port: MessagePort | null = null;

  /**
   * Observers that are called when the message port receives a message from the platform frame
   */
  private observers: Record<
    UILayerObservers,
    ((event: MessageEvent) => void)[]
  > = {scrolloutside: [], interactoutside: []};

  /**
   * Increases with every back-and-forth message between the UI layer and platform frame, which represents a unique identifier per message
   */
  private currentMessageId = 0;

  /**
   * Maps message ids to the callbacks that need to resolve/reject when messages are returned
   */
  private messageIdCallerMap = new Map<number, DeferredPromise<any>>();

  /**
   * Deferred promise indicating whether the data layer has loaded
   */
  public hasDataLayerLoaded: DeferredPromise<boolean> =
    new DeferredPromise<boolean>();

  /**
   * Creates a wrapper around the message port for the corresponding embedded component to communicate with the platform frame.
   * It sets up a listener on the message port to handle message events from the platform frame.
   * @param deferredAuthPromise Contains the promise that is resolved when the account session is claimed
   * @param deferredActions Map of StripeJS iframe names to deferred promises, that is resolved when the StripeJS financial connections flow is completed.
   * @param deferredFrames Map of accessory iframe names to deferred promises, that is resolved when the accessory iframe window object is loaded.
   * @param connectElementAttributes The HTML attributes of the corresponding embedded component.
   * @param connectJsOptions The ConnectJS options of the Connect instance.
   * @param onboardingState The onboarding state of the Connect instance.
   * @param platformViewportState The viewport dimensions of the platform frame.
   * @param loggedOutState The logged out state of the Connect instance.
   * @param renderedState The rendered state of the corresponding embedded component.
   */
  // eslint-disable-next-line no-useless-constructor
  constructor(
    private deferredAuthPromise: DeferredPromise<AccountSessionClaim>,
    private deferredActions: Record<string, DeferredPromise<any>>,
    private deferredFrames: Record<string, DeferredPromise<Window>>,
    private connectElementAttributes: ConnectElementAttributes,
    private connectJsOptions: ConnectJsOptions,
    private onboardingState: OnboardingState,
    private platformViewportState: PlatformViewportState,
    private loggedOutState: LoggedOutState,
    private renderedState: ConnectElementRenderedState,
    private connectElementSupplementalObjects: ConnectElementSupplementalObjects,
    private supplementalDataFunctions: ConnectElementSupplementalFunctions,
  ) {}

  initMessagePort() {
    return setupMessagePortHandoff<IUILayerInit>({
      messageReceiverDirection: 'to',
      messageReceiverType: 'ui-layer-message',
      messageReceiverInitRequestType: 'init',
      messageListener: this.createOnMessageChannelEventListener.bind(this),
      messageReceivedCallback: this.setValues.bind(this),
      acceptedOrigin: getPlatformOrigin(),
    })();
  }

  private setValues(
    portValues: MessagePortValues,
    _initValues: UILayerInitOptions,
  ) {
    this.port = portValues.port;
  }

  private createOnMessageChannelEventListener(_port: MessagePort) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    return (event: MessageEvent) =>
      setupMessagePortListener<IToUILayerMessage>({
        event,
        messageDirection: 'to',
        messageType: 'ui-layer-message',
        messageHandlers: {
          // This event receives the account session claim result from the platform frame, which unblocks rendering of the embedded component.
          claimresultupdate: (values) => {
            if (values === 'error') {
              self.deferredAuthPromise.reject(
                new Error(
                  'Failed to claim account session. This is expected if the account session is invalid, expired or has been used, or if an API/network error happens when claiming the account session.',
                ),
              );
            } else {
              self.deferredAuthPromise.resolve(values);
            }
          },
          // This event resolves as the window object of the requested accessory layer iframe, so that the deferred promise in UILayerWrapper.requestIframe can be resolved.
          // and is fired after the accessory layer iframe is added to the DOM.
          createdaccessorylayer: ({id}) => {
            const frame = getAccessoryLayer(id);
            if (frame) {
              const deferredFrame = self.deferredFrames[id];

              if (deferredFrame) {
                deferredFrame.resolve(frame);
              }
            } else {
              devLogger.error('Failed to find accessory frame', id);
            }
          },
          // This event deletes the accessory layer from the deferredFrames map, and is fired after the accessory layer iframe is removed from the DOM.
          deleteaccessorylayer: ({id}) => {
            delete self.deferredFrames[id];
          },
          // This resolves as the response of the StripeJS collect financial connections accounts API, so that the deferred promise in UILayerWrapper.collectFinancialConnectionsAccountsOverride can be resolved.
          collectfinancialconnectionsaccountsresult: ({id, result}) => {
            const deferredAction = self.deferredActions[id];
            if (deferredAction) {
              deferredAction.resolve(result);
            }
          },
          collectbankaccounttokenresult: ({id, result}) => {
            const deferredAction = self.deferredActions[id];
            if (deferredAction) {
              deferredAction.resolve(result);
            }
          },
          collectmobilefinancialconnectionsresult: ({id, result}) => {
            const deferredAction = self.deferredActions[id];
            if (deferredAction) {
              deferredAction.resolve(result);
            }
          },
          init: () => {
            devLogger.error('The UI layer has already claimed a message port.');
          },
          // This event fires a scrolloutside event when a scroll event is fired from the platform frame.
          scrolloutside: () => {
            this.fireObservers('scrolloutside', event);
          },
          // This event fires an interactoutside event when a click/press event is fired anywhere but from the UI layer iframe.
          interactoutside: () => {
            this.fireObservers('interactoutside', event);
          },
          // This event updates the onboarding state on the UI layer frame, after the onboarding state on the platform frame (ConnectElementBase) is updated.
          notifyonboardingstatechanged: (values) => {
            if (
              self.onboardingState.values.isOnboardingComplete ===
              values.isOnboardingComplete
            ) {
              // This is a no-op
              return;
            }

            self.onboardingState.updateValues(values);
          },
          // This event updates the logged out state on the UI layer frame, after the logged out state on the platform frame (ConnectElementBase) is updated.
          notifyloggedoutstatechanged: (values) => {
            if (self.loggedOutState.values.loggedOut === values.loggedOut) {
              // This is a no-op
              return;
            }

            self.loggedOutState.updateValues(values);
          },
          // This event updates the rendered state on the UI layer frame, after the rendered state on the platform frame (ConnectElementBase) is updated.
          updaterenderedstateto: ({isRendered}) => {
            if (self.renderedState.isRendered === isRendered) {
              // This is a no-op
              return;
            }

            self.renderedState.update(isRendered);
          },
          // This event updates the HTML attribute on the UI layer frame, after the HTML attribute associated with the custom element is updated.
          updatehtmlattribute: (values) => {
            self.connectElementAttributes.updateValues(values);
          },
          updatesupplementalobjects: (values) => {
            self.connectElementSupplementalObjects.updateValues(values);
          },
          // This event updates the init options on the UI layer frame, after the init options on the platform frame (ConnectElementBase) is updated.
          updateinitoptionsto: (values) => {
            self.connectJsOptions.updateValues(values);
          },
          // This event updates the init options on the UI layer frame, after the init options on the platform frame (ConnectElementBase) is updated.
          supplementalfunctioncallfinished: (props) => {
            const promiseCallback = this.messageIdCallerMap.get(
              props.messageId,
            );
            if (promiseCallback) {
              this.messageIdCallerMap.delete(props.messageId); // Remove entry from map
              if (props.values) {
                promiseCallback.resolve(props.values); // Resolve the promise
              } else if (props.serializableError) {
                const error = deserializeError(props.serializableError);
                promiseCallback.reject(error); // Reject the promise with an error
              } else {
                promiseCallback.reject(
                  new Error(
                    'Supplemental function call finished without values or error',
                  ),
                );
              }
            }
          },
          updatesupplementalfunctions: (props) => {
            // update new props...
            const supplementalDataFunctionsProxy =
              generateSupplementalFunctionsProxy(props, this);

            this.supplementalDataFunctions.updateValues(
              supplementalDataFunctionsProxy,
            );
          },
          // This event is fired after the following:
          // - The UI layer's FrameMessenger will call the refreshClientSecretUILayer function. This sends a message `refreshclientsecretfrom` to the platform frame
          // - The platform frame's FrameMessenger will call the `refresh` function if applicable and send the `authenticate` message to the Data layer
          // - The Data layer will call the claim API and return the results to the platform frame
          // - The platform frame will then send the `refreshclientsecretto` message to the UI layer, signifying that the data layer has the up-to-date API key
          refreshclientsecretto: ({messageId}) => {
            const promiseCallback = this.messageIdCallerMap.get(messageId);
            if (promiseCallback) {
              this.messageIdCallerMap.delete(messageId); // Remove entry from map
              promiseCallback.resolve(undefined); // Resolve the promise
            }
          },
          // This event resolves the deferred promise in UILayerWrapper.hasDataLayerLoaded, and is fired after the data layer iframe is loaded.
          datalayerloaded: () => {
            this.hasDataLayerLoaded.resolve(true);
          },
          platformviewportresize: (values) => {
            this.platformViewportState.updateValues(values);
          },
        },
      });
  }

  private fireObservers = (type: UILayerObservers, event: MessageEvent) => {
    this.observers[type].forEach((observer) => observer(event));
  };

  registerObserver = (
    type: 'interactoutside' | 'scrolloutside',
    observer: (event: MessageEvent) => void,
  ) => {
    this.observers[type].push(observer);
  };

  unregisterObserver = (
    type: 'interactoutside' | 'scrolloutside',
    observer: (event: MessageEvent) => void,
  ) => {
    this.observers[type] = this.observers[type].filter((o) => o !== observer);
  };

  private sendMessageFromUILayer<T extends IFromUILayerMessage>(
    requestType: T['requestType'],
    values?: T extends {values: unknown} ? T['values'] : undefined,
  ) {
    if (!this.port) {
      const error = new Error(
        'The port was not defined when sending a message to the UI layer',
      );
      getIframeErrorReporter().captureIframeException(error);
      throw error;
    }

    this.port.postMessage({
      type: 'ui-layer-message',
      direction: 'from',
      requestType,
      values,
    });
  }

  deleteAccessoryLayer(values: IUIDeleteAccessoryLayerFrom['values']) {
    this.sendMessageFromUILayer('deleteaccessorylayer', values);
  }

  readjustIframe(values: IUIReadjustIframe['values']) {
    this.sendMessageFromUILayer('readjustiframe', values);
  }

  updateConnectJSOptions(options: UpdateOptions) {
    this.sendMessageFromUILayer('updateinitoptionsfrom', options);
  }

  updateOnboardingState(values: OnboardingStateValues) {
    this.sendMessageFromUILayer('updateonboardingstate', values);
  }

  emitHTMLEvent(values: IConnectElementEvent) {
    this.sendMessageFromUILayer('emithtmlevent', values);
  }

  requestAccessoryLayer(
    id: string,
    type: LayerType,
    initialReposition?: IUILayerRepositionValues,
  ) {
    this.sendMessageFromUILayer('requestaccessorylayer', {
      id,
      type,
      sourceId: window.name,
      initialReposition,
    });
  }

  copyText(value: string) {
    this.sendMessageFromUILayer('copyText', value);
  }

  reloadWindow() {
    this.sendMessageFromUILayer('reloadWindow');
  }

  redirectWindow(url: string) {
    this.sendMessageFromUILayer('redirectWindow', url);
  }

  removeSearchParam(param: string) {
    this.sendMessageFromUILayer('removeSearchParam', param);
  }

  collectFinancialConnectionsAccounts(
    values: ICollectFinancialConnectionsAccounts,
  ) {
    this.sendMessageFromUILayer('collectfinancialconnectionsaccounts', values);
  }

  collectBankAccountToken(values: ICollectFinancialConnectionsAccounts) {
    this.sendMessageFromUILayer('collectbankaccounttoken', values);
  }

  deleteStripeJsLayer(id: string) {
    this.sendMessageFromUILayer('deletestripejslayer', {id});
  }

  callSupplementalFunction(
    values: Omit<IUILayerCallSupplementalFunctionParams, 'messageId'>,
    deferredPromise: DeferredPromise<any>,
  ) {
    const messageId = this.getNewMessageId();

    this.messageIdCallerMap.set(messageId, deferredPromise);
    this.sendMessageFromUILayer('callsupplementalfunction', {
      ...values,
      messageId,
    });
  }

  reportSuccess(successType: IUILayerReportSuccessValues) {
    this.sendMessageFromUILayer('reportsuccess', successType);
  }

  private getNewMessageId(): number {
    const {currentMessageId} = this;
    this.currentMessageId += 1;
    return currentMessageId;
  }

  public sendMessageAndWaitForReply<T extends IFromUILayerMessage, TReturnType>(
    requestType: T['requestType'],
    values?: T extends {values: unknown} ? T['values'] : undefined,
  ): Promise<TReturnType> {
    const messageId = this.getNewMessageId();

    const deferredPromise = new DeferredPromise<TReturnType>();
    this.messageIdCallerMap.set(messageId, deferredPromise);

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const messageValues = (values || {}) as (T extends {values: unknown}
      ? T['values']
      : undefined) & {
      messageId: number;
    };
    messageValues.messageId = messageId;
    this.sendMessageFromUILayer(requestType, messageValues);
    return deferredPromise.promise;
  }
}
